import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import './Header.css';

const sucursalesLinks = [
  { path: '/sucursales/polanco', label: 'Polanco', sub: 'Nail Bar' },
  { path: '/sucursales/del-valle', label: 'Del Valle', sub: 'Beauty Studio' },
  { path: '/sucursales/condesa', label: 'Condesa', sub: 'Beauty Studio' },
];

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;
  const isSucursalActive = () => sucursalesLinks.some(l => location.pathname === l.path);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileDropdownOpen(false);
  }, [location.pathname]);

  return (
    <header className="navigation-header">
      <div className="navigation-content">
        <Link to="/" className="navigation-logo" aria-label="Hera Beauty Station — Inicio">
          Hera Beauty Station
        </Link>

        {/* Desktop Navigation */}
        <nav className="navigation-menu" aria-label="Navegación principal">
          <Link to="/" className={`navigation-link ${isActive('/') ? 'active' : ''}`}>Inicio</Link>
          <Link to="/servicios" className={`navigation-link ${isActive('/servicios') ? 'active' : ''}`}>Servicios</Link>

          {/* Sucursales dropdown */}
          <div
            className={`nav-dropdown ${dropdownOpen ? 'open' : ''} ${isSucursalActive() ? 'active' : ''}`}
            ref={dropdownRef}
            onMouseEnter={() => setDropdownOpen(true)}
            onMouseLeave={() => setDropdownOpen(false)}
          >
            <button
              className={`navigation-link nav-dropdown-trigger ${isSucursalActive() ? 'active' : ''}`}
              onClick={() => setDropdownOpen(!dropdownOpen)}
              aria-expanded={dropdownOpen}
              aria-haspopup="true"
              data-testid="sucursales-dropdown-trigger"
            >
              Sucursales <ChevronDown size={14} className="nav-chevron" aria-hidden="true" />
            </button>
            <div className="nav-dropdown-menu" role="menu">
              {sucursalesLinks.map((l) => (
                <Link
                  key={l.path}
                  to={l.path}
                  className={`nav-dropdown-item ${isActive(l.path) ? 'active' : ''}`}
                  role="menuitem"
                  onClick={() => setDropdownOpen(false)}
                  data-testid={`sucursal-link-${l.label.toLowerCase().replace(' ', '-')}`}
                >
                  <span className="nav-dropdown-label">{l.label}</span>
                  <span className="nav-dropdown-sub">{l.sub}</span>
                </Link>
              ))}
            </div>
          </div>

          <Link to="/about" className={`navigation-link ${isActive('/about') ? 'active' : ''}`}>Nosotros</Link>
          <Link to="/contacto" className={`navigation-link ${isActive('/contacto') ? 'active' : ''}`}>Contacto</Link>
          <Link to="/wedding" className={`navigation-link nav-wedding ${isActive('/wedding') ? 'active' : ''}`} data-testid="header-wedding">Hera Weddings</Link>
          <Link to="/booking" className="navigation-link nav-reservar" data-testid="header-reservar">Reservar</Link>
        </nav>

        {/* Mobile toggle */}
        <button
          className="mobile-menu-toggle"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label={mobileMenuOpen ? 'Cerrar menú' : 'Abrir menú'}
          aria-expanded={mobileMenuOpen}
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <nav className="mobile-navigation" aria-label="Menú móvil">
          <Link to="/" className={`mobile-navigation-link ${isActive('/') ? 'active' : ''}`}>Inicio</Link>
          <Link to="/servicios" className={`mobile-navigation-link ${isActive('/servicios') ? 'active' : ''}`}>Servicios</Link>

          {/* Sucursales accordion */}
          <div className="mobile-accordion">
            <button
              className={`mobile-navigation-link mobile-accordion-trigger ${isSucursalActive() ? 'active' : ''}`}
              onClick={() => setMobileDropdownOpen(!mobileDropdownOpen)}
              aria-expanded={mobileDropdownOpen}
              data-testid="sucursales-mobile-trigger"
            >
              <span>Sucursales</span>
              <ChevronDown size={16} className={`nav-chevron ${mobileDropdownOpen ? 'rotated' : ''}`} aria-hidden="true" />
            </button>
            {mobileDropdownOpen && (
              <div className="mobile-accordion-items">
                {sucursalesLinks.map((l) => (
                  <Link
                    key={l.path}
                    to={l.path}
                    className={`mobile-accordion-item ${isActive(l.path) ? 'active' : ''}`}
                    data-testid={`sucursal-mobile-${l.label.toLowerCase().replace(' ', '-')}`}
                  >
                    <span className="mobile-accordion-label">{l.label}</span>
                    <span className="mobile-accordion-sub">{l.sub}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>

          <Link to="/about" className={`mobile-navigation-link ${isActive('/about') ? 'active' : ''}`}>Nosotros</Link>
          <Link to="/contacto" className={`mobile-navigation-link ${isActive('/contacto') ? 'active' : ''}`}>Contacto</Link>
          <Link to="/wedding" className={`mobile-navigation-link ${isActive('/wedding') ? 'active' : ''}`} data-testid="mobile-wedding">Hera Weddings</Link>
          <Link to="/booking" className="mobile-navigation-link mobile-nav-reservar" data-testid="mobile-reservar">Reservar</Link>
        </nav>
      )}
    </header>
  );
};

export default Header;
